import { useState } from "react";
import { Reveal } from "@/components/bits";
import { trackBoth } from "@/lib/meta";

const API = process.env.REACT_APP_BACKEND_URL;

// PDP subscribe block — sits under the brand-message section. Same form
// styling as the colour-drop notify banner; posts to the shared leads list.
export default function EmailCapture() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setState("error");
      setMsg("Enter a valid email address.");
      return;
    }
    setState("sending");
    setMsg("");
    try {
      const r = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: clean, source: "pdp-subscribe" }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setState("done");
      trackBoth("Lead", { content_name: "pdp-subscribe" });
    } catch {
      setState("error");
      setMsg("Something went wrong — try again.");
    }
  };

  return (
    <section
      data-testid="email-capture"
      className="px-4 sm:px-8 max-w-6xl mx-auto w-full"
    >
      <Reveal>
        <div className="max-w-2xl mx-auto text-center rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-8 mb-10 sm:mb-12">
          <p className="font-body font-bold text-[11px] tracking-[0.22em] uppercase text-ink/50">
            Stay in the loop
          </p>
          <h2 className="mt-2 font-display uppercase leading-[0.95] text-[7vw] sm:text-3xl">
            First dibs on the next drop.
          </h2>
          <p className="mt-2.5 text-sm sm:text-base text-ink/70 font-medium leading-relaxed">
            New colourways and drop dates, straight to your inbox.
          </p>
          {state === "done" ? (
            <p
              data-testid="email-capture-success"
              className="mt-5 mx-auto max-w-md rounded-2xl border-2 border-ink bg-[#FFD84D]/30 px-4 py-3 text-sm font-semibold"
            >
              You're in — we'll email you when the next colourway drops.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-5 mx-auto max-w-md">
              <div className="flex gap-2">
                <input
                  data-testid="email-capture-input"
                  data-clarity-mask="true"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 min-w-0 min-h-[48px] rounded-full border-2 border-ink bg-ivory px-4 text-sm font-medium placeholder:text-ink/35 focus:outline-none"
                />
                <button
                  data-testid="email-capture-submit"
                  type="submit"
                  disabled={state === "sending"}
                  className="shrink-0 min-h-[48px] px-5 rounded-full bg-ink text-ivory font-display uppercase text-sm tracking-wide transition-transform duration-150 active:scale-95 disabled:opacity-60"
                >
                  {state === "sending" ? "…" : "Subscribe"}
                </button>
              </div>
              {msg && (
                <p
                  data-testid="email-capture-error"
                  role="alert"
                  className="mt-2 text-sm font-medium text-[#C2271B]"
                >
                  {msg}
                </p>
              )}
            </form>
          )}
        </div>
      </Reveal>
    </section>
  );
}
